import React, { useCallback, useEffect, useState } from "react";
import Spacing from "../../Spacing";
import { useDocumentTitle } from "../../../hooks/customDocumentTitleHook";
import { can_visit, debounce, getPractice, notify } from "../../../helpers";
import { Plus, Search } from "lucide-react";
import DataTablePagination from "../../DataTablePagination";
import InsurancesDataTable from "../../DataTable/InsurancesDataTable";
import { deleteInsuranceGroup, fetchAllInsurances } from "../../../services/insurance";
import AddAndEditInsuranceGroupModal from "../../Modal/AddAndEditInsuranceGroupModal";

export default function InsuranceGroups({selectedPracticeChange, fetchChangePracticeData}) {
  let account_id = getPractice("account_id");
  let location_id = getPractice("location_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    account_id = selectedPracticeChange?.account_id;
    location_id = selectedPracticeChange?.location_id;
  }

  const [enableLoader, setEnableLoader] = useState(false);
  const [insurancesData, setInsurancesData] = useState([]);
  const [newGroupModalOpen, setNewGroupModalOpen] = useState(false);
  const [editGroupModalOpen, setEditGroupModalOpen] = useState(false);
  const [groupEditData, setGroupEditData] = useState({});
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  const defaultFilter = {
    account_id: account_id,
    location_id: location_id,
    page: 1,
    per_page: 10,
    search_term: "",
    insurance_ids: [],
    order_by: [],
  };
  const [filter, setFilter] = useState(defaultFilter);
  const [dataCount, setDataCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });

  useEffect(() => {
    fetchInsurancesData(filter);
  }, [filter]);

  const fetchInsurancesData = async (allfilter) => {
    setEnableLoader(true);
    try {
      const response = await fetchAllInsurances(allfilter);
      if (response) {
        if(response?.data){
          setInsurancesData(response?.data?.results);
          setDataCount({
            totalCount: response?.data?.count,
            totalPages: response?.data?.total_pages,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setEnableLoader(false);
    }
  };

  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setEnableLoader(true);
    debouncedSetFilter(newFilter);
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setEnableLoader(true);
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setEnableLoader(true);
  };

  const handleDeleteRecord = async (id) => {
    if(can_visit("administrator") && can_visit("administrator_insurance_group")){
      try {
        setEnableLoader(true);
        await deleteInsuranceGroup(id, account_id, location_id);
        notify("success", "Record deleted successfully.", 0.5);
        await fetchInsurancesData(filter);
      } catch (error) {
        setEnableLoader(false);
        // console.log("🚀 ~ handleDeleteRecord ~ error:", error);
      }
    }
  };

  useDocumentTitle("Insurance Groups");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24 docpro_mb_0">
          Insurance Groups
        </h2>
        <div className="docpro_page_heading_btns">
          {(can_visit("administrator") && can_visit("administrator_insurance_group")) && (
            <button
              className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn"
              onClick={() => {
                setNewGroupModalOpen(true);
                setPreviousScrollPosition(window.scrollY);
                window.scrollTo(0, 0);
              }}
            >
              <Plus width={20} height={20} />
              <span>Add Group</span>
            </button>
          )}
        </div>
      </div>
      <Spacing lg="10" sm="15" />

      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings">
          <form className="docpro_search_form width_100">
            <input
              type="text"
              placeholder="Search"
              className="docpro_search_input"
              onChange={handleChangeSearchTerm}
            />
            <button className="docpro_search_btn">
              <Search width={20} height={20} />
            </button>
          </form>
        </div>

        <InsurancesDataTable
          tableData={insurancesData}
          reloadData={fetchInsurancesData}
          reloadDataFilter={filter}
          dataLoading={enableLoader}
          setDataLoading={setEnableLoader}
          setEditGroupModalOpen={setEditGroupModalOpen}
          setGroupEditData={setGroupEditData}
          handleDeleteRecord={handleDeleteRecord}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
        />

        {(insurancesData?.length > 0 && !enableLoader) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={dataCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={dataCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>


      {newGroupModalOpen &&
        <AddAndEditInsuranceGroupModal
          account_id = {account_id}
          location_id = {location_id}
          reloadData = {fetchInsurancesData}
          reloadDataFilter = {filter}
          editModal = {false}
          activeModal = {newGroupModalOpen}
          setActiveModal = {setNewGroupModalOpen}
          previousScrollPosition={previousScrollPosition}
        />
      }

      {editGroupModalOpen &&
        <AddAndEditInsuranceGroupModal
          account_id = {account_id}
          location_id = {location_id}
          reloadData = {fetchInsurancesData}
          reloadDataFilter = {filter}
          editModal = {true}
          editValue = {groupEditData}
          activeModal = {editGroupModalOpen}
          setActiveModal = {setEditGroupModalOpen}
          previousScrollPosition={previousScrollPosition}
        />
      }
    </>
  );
}
